import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Check, Close, DeleteOutline, FileDownload } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { nanoid } from '@reduxjs/toolkit';
import IconButton from '@mui/material/IconButton';
import { filesize } from 'filesize';

import { PostBinaryFile } from 'engine/baseApiClient';
import { downloadFile } from 'utility/downloadFile';
import urls from 'engine/urls';
import { resources } from 'oems/resources';

import * as Styled from './Attachments.styles';
import Tooltip from '@mui/material/Tooltip';
import { Stack } from '@mui/material';

export const Attachments = ({ form, defaultAttachments }) => {
  const { setFieldValue, errors } = form;

  const [files, setFiles] = useState([]);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    setAttachments(defaultAttachments.map((item) => ({ ...item, status: 'success' })));
  }, [defaultAttachments]);

  useEffect(() => {
    setFieldValue('attachments', attachments);
  }, [attachments, setFieldValue]);

  const onAddFiles = (acceptedFiles) => {
    const newFiles = acceptedFiles.map((file) => ({
      id: nanoid(),
      fileName: file.name,
      contentType: file.type,
      bytesSize: file.size,
      status: 'pending',
      file,
    }));

    setFiles((prevState) => [...newFiles, ...prevState]);

    newFiles.forEach(async (newFile) => {
      try {
        const response = await PostBinaryFile(newFile.fileName, newFile.file);       
        if (response.status === 'OK') {
          setFiles((prevState) => prevState.filter((item) => item.id !== newFile.id));
          setAttachments((prevState) => [{ ...response }, ...prevState]);
        } else { 
          throw response.detail;
        }
      } catch (error) {       
        setFiles((prevState) =>
          prevState.map((item) => (item.id === newFile.id ? { ...item, status: 'error', message:error} : item)),
        );
      }
    });
  };

  const {
    getRootProps,
    getInputProps,
    open: selectFile,
    isDragActive,
  } = useDropzone({
    onDrop: onAddFiles,
    noClick: true,
    noKeyboard: true,
  });

  const columns = [
    {
      field: 'fileName',
      headerName: 'Nazwa',
      minWidth: 200,
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'bytesSize',
      headerName: 'Rozmiar',
      headerAlign: 'center',
      align: 'center',
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (props) => filesize(props.row.bytesSize),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (props) => (
        <>
          {props.row.status === 'pending' ? (
            <Styled.CircularProgress />
          ) : props.row.status === 'error' ? (
            <>
            <Tooltip  title={props.row.message}>
              <Stack direction={'row'}  justifyContent="flex-start"  alignItems="center"> 
                <Styled.StatusIcon className="error">
                  <Close />
                </Styled.StatusIcon>
                Błąd
              </Stack>
            </Tooltip>             
            </>
          ) : (
            <>
              <Styled.StatusIcon className="success">
                <Check />
              </Styled.StatusIcon>
              Przesłano
            </>
          )}
        </>
      ),
    },
    {
      field: 'actions',
      headerName: 'Akcje',
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      disableColumnMenu: true,
      renderCell: (props) => (
        <>
          {!!props.row.uri && (
            <IconButton
              onClick={() => downloadFile(`${urls().root}${props.row.uri}`, props.row.contentType, props.row.fileName)}
            >
              <FileDownload />
            </IconButton>
          )}
          <IconButton
            color="error"
            onClick={() =>
              props.row.status === 'success'
                ? setAttachments((prevState) => prevState.filter((item) => item.id !== props.row.id))
                : setFiles((prevState) => prevState.filter((item) => item.id !== props.row.id))
            }
          >
            <DeleteOutline />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Styled.Wrapper {...getRootProps()}>
      <input {...getInputProps()} />

      {isDragActive && <Styled.DragOverPlaceholder />}

      {attachments.length === 0 && files.length === 0 ? (
        <Styled.Dropzone className={errors.attachments === ' ' ? 'error' : ''}>
          <button onClick={selectFile}>
            <div className="icon">
              <resources.AttachmentAddIcon />
            </div>
            Dodaj załącznik
          </button>
          lub przeciągnij i upuść plik tutaj.<br />
          Maksymalny rozmiar pliku to 10MB.
        </Styled.Dropzone>
      ) : (
        <>
          <Styled.DataGrid
            rows={[...files, ...attachments]}
            columns={columns}
            autoHeight
            getRowHeight={() => 'auto'}
            hideFooter
          />
          <Styled.AddAttachmentButton variant="text" onClick={selectFile}>
            <resources.AttachmentAddIcon />
            Dodaj załącznik
          </Styled.AddAttachmentButton>
        </>
      )}
    </Styled.Wrapper>
  );
};

Attachments.propTypes = {
  form: PropTypes.object.isRequired,
  defaultAttachments: PropTypes.array.isRequired,
};
